import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, useStaticQuery, graphql } from "gatsby";
import { ThemeProvider } from "styled-components";
import styled from "styled-components";
import CookieConsent, { Cookies } from "react-cookie-consent";
import { GlobalStyle } from "../style/global.js";
import GoogleTagManager from "./google-tagmanager.js";
import Header from "./header.js";
import Footer from "./footer.js";
import IconLibrary from "./icon-library";
import PromoBanner from "./promobanner.js";
import SyncCart from "./sync-cart";
import TopHeader from "./top-header.js";
import PrivateOrBusinessPopUp from "./private-business-popup.js";

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  max-width: 1920px;
  margin: 0 auto;
  font-family: var(--font-paragraphs);
`;

const TopHeaderWrapper = styled.div`
  background-color: ${({ theme }) =>
    theme.colors.uspBackground || `var(--color-white)`};
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.uspBorderBottom || `var(--color-primary)`};
  max-width: 1920px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-flow: row wrap;
  height: auto;
  align-items: center;
  justify-content: space-between;
`;

export const ConsentContext = React.createContext(null);
export const ProductContext = React.createContext(null);

export default function Layout({ children, staticQueryData, location }) {
  const { wp } =
    staticQueryData ||
    useStaticQuery(graphql`
      query LayoutQuery {
        wp {
          crbThemeOptions {
            crbPaddingBottom
            crbPaddingLeft
            crbPaddingRight
            crbPaddingTop
            crbPromoEnabled
            crbPromoText
            crbBorderRadius
            crbBorderRadiusBottomLeft
            crbBorderRadiusBottomRight
            crbBorderRadiusTopLeft
            crbBorderRadiusTopRight
            crbSameBorderRadius {
              value
            }
            crbUsp {
              icon {
                json
              }
              text
              url
            }
            crbUspAdditional {
              icon {
                json
              }
              text
              url
            }
            crbColorUspBackground
            crbColorUspBorderBottom
            crbColorUspIcon
            crbColorUspText
            crbColorAccent1
            crbColorAccent2
            crbColorAccent3
            crbColorBlack
            crbColorPrimary
            crbColorSecondary
            crbColorWhite
            crbColorHeaderBackground
            crbColorHeaderLink
            crbColorHeaderButton
            crbColorHeaderButtonText
            crbColorHeaderSearch
            crbColorFooterBackgroundTop
            crbColorFooterBackgroundBottom
            crbColorFooterTextTop
            crbColorFooterTextBottom
            crbColorFooterButton
            crbColorFooterButtonText
            crbColorBody
            crbColorBodyText
            crbColorBodyLink
            crbFontHeadingsWoff {
              title
              localFile {
                publicURL
              }
            }
            crbFontHeadingsWoff2 {
              title
              localFile {
                publicURL
              }
            }
            crbFontLinksWoff {
              title
              localFile {
                publicURL
              }
            }
            crbFontLinksWoff2 {
              title
              localFile {
                publicURL
              }
            }
            crbFontParagraphWoff {
              title
              localFile {
                publicURL
              }
            }
            crbFontParagraphWoff2 {
              title
              localFile {
                publicURL
              }
            }
            crbExtraFeatureWoocommerce
          }
          wcSettings {
            checkoutUrl
          }
          customCSS
        }
      }
    `);

  const [buttonBorderRadius] = React.useState(() => {
    if (wp.crbThemeOptions.crbSameBorderRadius.value === "yes") {
      return `${wp.crbThemeOptions.crbBorderRadius}px`;
    } else {
      return `${wp.crbThemeOptions.crbBorderRadiusTopLeft}px ${wp.crbThemeOptions.crbBorderRadiusTopRight}px ${wp.crbThemeOptions.crbBorderRadiusBottomRight}px ${wp.crbThemeOptions.crbBorderRadiusBottomLeft}px`;
    }
  });

  const theme = {
    name: "Default Theme from WP",
    colors: {
      primary: `${wp.crbThemeOptions.crbColorPrimary}`,
      secondary: `${wp.crbThemeOptions.crbColorSecondary}`,
      black: `${wp.crbThemeOptions.crbColorBlack}`,
      white: `${wp.crbThemeOptions.crbColorWhite}`,
      accent1: `${wp.crbThemeOptions.crbColorAccent1}`,
      accent2: `${wp.crbThemeOptions.crbColorAccent2}`,
      accent3: `${wp.crbThemeOptions.crbColorAccent3}`,
      body: `${wp.crbThemeOptions.crbColorBody}`,
      bodyText: `${wp.crbThemeOptions.crbColorBodyText}`,
      bodyLink: `${wp.crbThemeOptions.crbColorBodyLink}`,
      darkGray: "#333",
      lightGray: "#6e6e6e",
      headerBackground: `${wp.crbThemeOptions.crbColorHeaderBackground}`,
      headerLink: `${wp.crbThemeOptions.crbColorHeaderLink}`,
      headerButton: `${wp.crbThemeOptions.crbColorHeaderButton}`,
      headerButtonText: `${wp.crbThemeOptions.crbColorHeaderButtonText}`,
      headerSearch: `${wp.crbThemeOptions.crbColorHeaderSearch}`,
      footerBackgroundTop: `${wp.crbThemeOptions.crbColorFooterBackgroundTop}`,
      footerTextTop: `${wp.crbThemeOptions.crbColorFooterTextTop}`,
      footerBackgroundBottom: `${wp.crbThemeOptions.crbColorFooterBackgroundBottom}`,
      footerTextBottom: `${wp.crbThemeOptions.crbColorFooterTextBottom}`,
      footerButton: `${wp.crbThemeOptions.crbColorFooterButton}`,
      footerButtonText: `${wp.crbThemeOptions.crbColorFooterButtonText}`,
      uspBackground: `${wp.crbThemeOptions.crbColorUspBackground}`,
      uspBorderBottom: `${wp.crbThemeOptions.crbColorUspBorderBottom}`,
      uspText: `${wp.crbThemeOptions.crbColorUspText}`,
      uspIcon: `${wp.crbThemeOptions.crbColorUspIcon}`,
    },
    fontHeadings: {
      woff: `${wp.crbThemeOptions.crbFontHeadingsWoff?.localFile?.publicURL}`,
      woff2: `${wp.crbThemeOptions.crbFontHeadingsWoff2?.localFile?.publicURL}`,
      fontFamily: `${
        wp.crbThemeOptions.crbFontHeadingsWoff?.localFile?.publicURL &&
        wp.crbThemeOptions.crbFontHeadingsWoff2?.localFile?.publicURL
          ? wp.crbThemeOptions.crbFontHeadingsWoff?.title
          : "Arial, sans-serif"
      }`,
    },
    fontLinks: {
      woff: `${wp.crbThemeOptions.crbFontLinksWoff?.localFile?.publicURL}`,
      woff2: `${wp.crbThemeOptions.crbFontLinksWoff2?.localFile?.publicURL}`,
      fontFamily: `${
        wp.crbThemeOptions.crbFontLinksWoff?.localFile?.publicURL &&
        wp.crbThemeOptions.crbFontLinksWoff2?.localFile?.publicURL
          ? wp.crbThemeOptions.crbFontLinksWoff?.title
          : "Arial, sans-serif"
      }`,
    },
    fontParagraphs: {
      woff: `${wp.crbThemeOptions.crbFontParagraphWoff?.localFile?.publicURL}`,
      woff2: `${wp.crbThemeOptions.crbFontParagraphWoff2?.localFile?.publicURL}`,
      fontFamily: `${
        wp.crbThemeOptions.crbFontParagraphWoff?.localFile?.publicURL &&
        wp.crbThemeOptions.crbFontParagraphWoff2?.localFile?.publicURL
          ? wp.crbThemeOptions.crbFontParagraphWoff?.title
          : "Arial, sans-serif"
      }`,
    },
    typography: {
      fontFamily: "Arial, sans-serif",
    },
    breakpoints: {
      wide: "1180px",
      wideTablet: "1025px",
      tablet: "850px",
      phone: "600px",
    },
    borderRadius: buttonBorderRadius ? buttonBorderRadius : "0px",
    customCSS: wp.customCSS,
  };

  const [consent, setConsent] = React.useState({
    googleMaps: undefined,
    googleTagManager: undefined,
  });
  const [showPopUp, setShowPopUp] = useState(
    typeof window !== "undefined" &&
      localStorage.getItem("private-business") === null
  );

  const toKebabCase = (str) => str.replace(/[A-Z]/g, "-$&").toLowerCase();
  const toCamelCase = (str) => str.replace(/-./g, (x) => x[1].toUpperCase());

  const updateConsent = (values) => {
    if (process.env.GATSBY_CONSENT_SCRIPT_URL) {
      const eventDetail = Object.keys(values).reduce((choices, key) => {
        choices[toKebabCase(key)] = values[key] ? "granted" : "denied";
        return choices;
      }, {});

      document.dispatchEvent(
        new CustomEvent("update-mev-consent", { detail: eventDetail })
      );
    } else {
      Object.keys(values).forEach((key) => {
        Cookies.set(`consent_${key}`, values[key].toString(), { expires: 365 });
      });
    }

    setConsent({ ...consent, ...values });
  };

  const handleMevConsent = (event) => {
    const newConsent = Object.keys(event.detail).reduce((choices, key) => {
      choices[toCamelCase(key)] = event.detail[key] === "granted";
      return choices;
    }, {});

    setConsent({ ...consent, ...newConsent });
  };

  const checkPrivateOrBusiness = () => {
    setShowPopUp(localStorage.getItem("private-business") === null);
  };

  useEffect(() => {
    const mevConsentListener = (event) => handleMevConsent(event);

    window.addEventListener("storage", checkPrivateOrBusiness);
    if (process.env.GATSBY_CONSENT_SCRIPT_URL) {
      document.addEventListener("mev-consent", mevConsentListener);
    }

    const consentCookieValues = Object.keys(consent).reduce((values, key) => {
      values[key] = Cookies.get(`consent_${key}`) === "true";
      return values;
    }, {});

    setConsent({ ...consent, ...consentCookieValues });

    return () => {
      document.removeEventListener("mev-consent", mevConsentListener);
      removeEventListener("storage", checkPrivateOrBusiness);
    };
  }, []);

  const [favouriteList, setFavouriteList] = useState([]);

  useEffect(() => {
    setFavouriteList(
      JSON.parse(localStorage.getItem("favourite-list") || "[]")
    );
  }, []);

  useEffect(() => {
    localStorage.setItem("favourite-list", JSON.stringify(favouriteList));
    if (favouriteList === "[]") {
      localStorage.removeItem("favourite-list");
    }
  }, [favouriteList]);

  useEffect(() => {
    if (
      navigator.userAgent.includes("Safari") &&
      navigator.userAgent.includes("iPhone")
    ) {
      document.body.style.overflowX = "hidden";
      const layout = document.querySelector(".layout");
      if (layout) {
        layout.style.width = "100vw";
        layout.style.boxSizing = "border-box";
      }
    }
  }, []);

  return (
    <ConsentContext.Provider value={{ consent, updateConsent }}>
      <ProductContext.Provider value={{ favouriteList, setFavouriteList }}>
        {!process.env.GATSBY_CONSENT_SCRIPT_URL && (
          <>
            <CookieConsent
              buttonText="Jeg samtykker"
              declineButtonText="Jeg samtykker ikke"
              declineButtonClasses="button secondary"
              buttonClasses="button"
              cookieName="consentBanner_v2"
              enableDeclineButton
              disableStyles
              onAccept={() =>
                updateConsent({
                  googleMaps: true,
                  googleTagManager: true,
                })
              }
              onDecline={() =>
                updateConsent({
                  googleMaps: false,
                  googleTagManager: false,
                })
              }
            >
              <p>
                Dette nettstedet trenger informasjonskapsler og
                tredjepartsskript for full funksjonalitet og analyse. For mer
                informasjon, les vår{" "}
                <Link to="/personvern/" style={{ color: "white" }}>
                  personvernpolicy
                </Link>
                .
              </p>
            </CookieConsent>
            <GoogleTagManager />
          </>
        )}
        <ThemeProvider theme={theme}>
          {showPopUp && <PrivateOrBusinessPopUp />}
          <IconLibrary />
          <Header
            staticQueryData={staticQueryData}
            location={location}
            staticSearchData={staticQueryData}
          >
            <PromoBanner
              promoEnabled={wp.crbThemeOptions.crbPromoEnabled}
              promoText={wp.crbThemeOptions.crbPromoText}
            />

            <TopHeaderWrapper>
              <TopHeader />
            </TopHeaderWrapper>
          </Header>
          <GlobalStyle />
          <Wrapper>{children}</Wrapper>
          <Footer staticQueryData={staticQueryData} />
          {wp?.crbThemeOptions?.crbExtraFeatureWoocommerce && <SyncCart />}
        </ThemeProvider>
      </ProductContext.Provider>
    </ConsentContext.Provider>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
  staticQueryData: PropTypes.object,
  location: PropTypes.object,
};

Layout.defaultProps = {
  children: undefined,
  staticQueryData: undefined,
  location: undefined,
};
